import { useMemo, useRef, useState, useEffect } from "react";
import Logo from "./logo";
import NavMenu from "./navmenu";
import MobileMenu from "./mobile-menu";
import ProfileButton from "../ui/profile-button";
import TokenCard from "../ui/token-card";
import { useAuth } from "@/hooks/use-auth";
import { Menu, X } from "lucide-react";
import ProfileMenu from "../ui/profile-menu";
import { useClickOutside } from "@/hooks/use-click-outside";
import paths from "@/routes/paths";
import ChainSwitch from "./chain-switch";
import { useWallet } from "@/hooks/use-wallet";
import { chains } from "@/utils/chains";
import { useNavigate } from "react-router";
import { setChainSwitching } from "@/utils/helpers/transaction-error";

const navs: NavItem[] = [
  { label: "Home", path: paths.dashboard, newTab: false },
  { label: "Quests", path: paths.quests.root, newTab: false },
  { label: "Leaderboard", path: paths.leaderboard.root, newTab: false },
  { label: "Referral", path: paths.referral.root, newTab: false },
  { label: "Marketplace", path: paths.nft.root, newTab: false },
];

const menuItems: any[] = [
  { label: "My Profile", icon: <></>, path: paths.user.profile },
  { label: "My Items", icon: <></>, path: paths.user.items },
  { label: "My Entrypass", icon: <></>, path: paths.user.entrypasses },
  { label: "Recent Transactions", icon: <></>, path: paths.user.transactions },
  { label: "Logout", icon: <></>, path: paths.logout },
];

const Navbar = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [isActiveMenu, setIsActiveMenu] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isNetworkDropdownOpen, setIsNetworkDropdownOpen] =
    useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const networkDropdownRef = useRef<HTMLDivElement>(null);
  const networkButtonRef = useRef<HTMLDivElement>(null);
  const wallet = useWallet();
  const previousChainIdRef = useRef<number | undefined>(wallet.chainId);

  const chain = useMemo(
    () =>
      chains.find((item: any) => String(item.id) === String(wallet.chainId)),
    [wallet.chainId],
  );

  useEffect(() => {
    previousChainIdRef.current = wallet.chainId;
  }, [wallet.chainId]);

  const onMenuClick = () => setIsActiveMenu((menu: boolean) => !menu);
  const onMobileMenuToggle = () => setIsMobileMenuOpen((prev) => !prev);
  const onNetworkDropdownToggle = () =>
    setIsNetworkDropdownOpen((prev) => !prev);

  useClickOutside(menuRef, onMenuClick, buttonRef);
  useClickOutside(
    networkDropdownRef,
    () => setIsNetworkDropdownOpen(false),
    networkButtonRef,
  );

  const onNetworkSwitch = () => {
    onNetworkDropdownToggle();
  };

  const handleChainSelect = async (selectedChain: any) => {
    try {
      const chainId = parseInt(selectedChain.id);
      setChainSwitching(true);
      await wallet.switchChain(chainId);
      setIsNetworkDropdownOpen(false);
    } catch (error) {
      console.error("Failed to switch chain:", error);
    } finally {
      setChainSwitching(false);
    }
  };

  return (
    <>
      <header className="text-white bg-black/20 backdrop-blur-2xl  flex flex-row 2xl:container 2xl:mx-auto  justify-between top-0 sm:pt-6 pt-3 pb-3 z-99 fixed w-full left-1/2 -translate-x-1/2  sm:px-10 px-4 2xl:px-0">
        <div className="flex flex-row items-center gap-x-10 xl:gap-x-24">
          <Logo onClick={() => navigate(paths.dashboard)} />
          <NavMenu navs={navs} />
        </div>

        <div className="flex flex-row items-center gap-x-2 relative">
          <div ref={networkButtonRef} className="relative">
            <ChainSwitch
              onClick={onNetworkSwitch}
              logoURI={chain?.tokenImg!}
              chain={chain}
            />
            {isNetworkDropdownOpen && (
              <div
                ref={networkDropdownRef}
                className="absolute border-4 bg-dark-2 rounded-2xl border-primary-1 top-12 left-0 z-50 min-w-max max-h-125 overflow-y-auto scrollbar-hide animate-in fade-in duration-200"
              >
                <div className="sticky top-0 px-4 py-3 border-b border-white/10 bg-linear-to-r from-primary-1/10 to-transparent">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                    Select Network
                  </p>
                </div>
                {chains.map((networkChain: any, index: number) => (
                  <button
                    key={networkChain.id}
                    onClick={() => handleChainSelect(networkChain)}
                    className={`w-full flex items-center gap-x-3 px-4 py-3.5 transition-all duration-300 group relative overflow-hidden ${
                      String(networkChain.id) === String(wallet.chainId)
                        ? "bg-linear-to-r from-primary-1/30 to-primary-1/10"
                        : "hover:bg-white/5"
                    } ${index !== chains.length - 1 ? "border-b border-white/5" : ""}`}
                  >
                    {/* Background gradient on hover */}
                    <div className="absolute inset-0 bg-linear-to-r from-primary-1/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Content */}
                    <div className="relative flex items-center gap-x-3 w-full">
                      {/* Chain Logo */}
                      <div className="shrink-0">
                        <img
                          src={networkChain.tokenImg}
                          alt={networkChain.name}
                          className="h-6 w-6 rounded-full ring-1 ring-white/20 group-hover:ring-primary-1/50 transition-all duration-300 group-hover:scale-110"
                        />
                      </div>

                      {/* Chain Name */}
                      <span
                        className={`text-sm font-medium transition-colors duration-300 ${
                          String(networkChain.id) === String(wallet.chainId)
                            ? "text-primary-1 font-semibold"
                            : "text-white/70 group-hover:text-white"
                        }`}
                      >
                        {networkChain.name}
                      </span>

                      {/* Checkmark for selected */}
                      {String(networkChain.id) === String(wallet.chainId) && (
                        <div className="ml-auto flex-shrink-0">
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary-1/30 border border-primary-1 animate-pulse">
                            <svg
                              className="w-3 h-3 text-primary-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}

                {/* Footer Info */}
                <div className="sticky bottom-0 px-4 py-3 border-t border-white/10 bg-gradient-to-t from-primary-1/5 to-transparent">
                  <p className="text-xs text-white/40">
                    Current:{" "}
                    <span className="text-primary-1 font-semibold">
                      {chain?.name}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
          <TokenCard value={auth.tokens} />
          <div ref={buttonRef}>
            <ProfileButton
              label={auth?.user?.username}
              onClick={onMenuClick}
              isMenuActive={isActiveMenu}
            />
          </div>
          <ProfileMenu
            ref={menuRef}
            isActive={isActiveMenu}
            list={menuItems}
            onClose={() => setIsActiveMenu(false)}
            className="absolute top-10 lg:right-0 right-12 backdrop-blur-sm bg-black/20 border-primary-1"
          />
          <button
            ref={hamburgerRef}
            onClick={onMobileMenuToggle}
            className="lg:hidden p-2 rounded-md hover:bg-white/10 transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>
      </header>
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navs={navs}
        hamburgerRef={hamburgerRef}
      />
    </>
  );
};

export default Navbar;
